import React, { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import { CirclePlus, Delete, DeleteIcon, Edit, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Separator } from "../../components/ui/separator";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination";
import axios from "axios";
import { toast, Toaster } from "sonner";

const token = localStorage.getItem("adminToken");
const ITEMS_PER_PAGE = 10;

const Spinner = () => (
  <div className="inline-block w-5 h-5 border-4 border-t-transparent border-gray-600 rounded-full animate-spin"></div>
);

const Banner = () => {
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [updateCategory, setUpdateCategory] = useState("");
  const [file, setFile] = useState();
  const [updateFile, setUpdateFile] = useState();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
  };

  const handleUpdateFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setUpdateFile(selectedFile);
  };

  const fetchCategories = async () => {
    try {
      setIsLoading(true)
      const res = await axios.get(
        "https://wavetechservices.in/api/admin/banners",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setCategories(res.data.banners);
      setFilteredCategories(res.data.banners);
      setIsLoading(false)
      setTotalPages(Math.ceil(res.data.banners.length / ITEMS_PER_PAGE));
    } catch (error) {
      console.error("Error fetching banners:", error);
      toast.error("Failed to fetch banners");
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    // Filter categories based on search term
    if (searchTerm.trim() === "") {
      setFilteredCategories(categories);
      setTotalPages(Math.ceil(categories.length / ITEMS_PER_PAGE));
      setCurrentPage(1);
    } else {
      const filtered = categories.filter(cat =>
        cat.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCategories(filtered);
      setTotalPages(Math.ceil(filtered.length / ITEMS_PER_PAGE));
      setCurrentPage(1);
    }
  }, [searchTerm, categories]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleUpload = async () => {
    setIsSubmitting(true)
    if (!file) {
      toast.error("Please select a file!");
      setIsSubmitting(false)
      return;

    }

    const formDataToSend = new FormData();
    formDataToSend.append("image", file);
    formDataToSend.append("title", category);

    try {
      const response = await axios.post(
        // "https://wavetechservices.in/api/admin/banners",
        "https://wavetechservices.in/api/admin/banners",
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setIsSubmitting(false)
      setOpenAdd(false);
      setCategory("");
      setFile(null);
      fetchCategories();
      toast.success("Banner uploaded successfully!");
    } catch (error) {
      console.error("Upload Error:", error);
      toast.error("Upload failed!");
      setIsSubmitting(false)
    }
  };

  const handleUpdate = async (category) => {
    setIsSubmitting(true)
    const formDataToSend = new FormData();
    updateFile && formDataToSend.append("image", updateFile);
    updateCategory && formDataToSend.append("title", updateCategory);

    try {
      const response = await axios.put(
        `https://wavetechservices.in/api/admin/banners/${category._id}`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setIsSubmitting(false)
      setOpenEdit(false);
      setUpdateCategory("");
      setUpdateFile(null);
      fetchCategories();
      toast.success("Banner updated successfully!");
    } catch (error) {
      console.error("Upload Error:", error);
      toast.error("Banner update failed!");
      setIsSubmitting(false)
    }
  };

  const handleDelete = async (category) => {
    try {
      setIsSubmitting(true)
      const res = await axios.delete(
        `https://wavetechservices.in/api/admin/banners/${category._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setIsSubmitting(false)
      setOpenDelete(false);
      fetchCategories();
      toast.success("Banner deleted successfully!");
    } catch (err) {
      console.error(err, "error");
      setIsSubmitting(false)
      toast.error("Banner deletion failed!");
    }
  };

  // Get current items for pagination
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentItems = filteredCategories.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <div className="px-[1vw] my-5">
      <Toaster />
      <div className="flex lg:justify-between lg:items-center flex-col lg:flex-row ">
        <h1 className="text-2xl">Banners</h1>
        <div className="flex lg:gap-4 items-center gap-2 my-3 lg:my-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search banners..."
              className="pl-10"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <Dialog open={openAdd} onOpenChange={setOpenAdd}>
            <DialogTrigger>
              <div className="flex items-center">
                <Button className="bg-black px-5 text-white h-9 hover:opacity-90">
                  Add <CirclePlus className="size-4 ml-1" />
                </Button>
              </div>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle className="lg:text-2xl text-xl">
                  Add Banner
                </DialogTitle>
              </DialogHeader>
              <div className="flex flex-col py-4 space-y-3">
                <div className="flex items-center gap-4">
                  <Label htmlFor="name" className="w-44">
                    Title
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="col-span-3"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <Label htmlFor="icon" className="w-44">
                    Banner Image
                  </Label>
                  <Input
                    id="icon"
                    type="file"
                    onChange={handleFileChange}
                    className="col-span-3"
                    accept="image/*"
                  />
                </div>
              </div>
              <div className="flex justify-center items-center">
                <Button
                  type="submit"
                  className="bg-black px-5 text-white h-9 hover:opacity-90 flex items-center justify-center gap-2"
                  onClick={handleUpload}
                  disabled={isLoading || !category || !file}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner />
                      Adding...
                    </>
                  ) : (
                    "Add Banner"
                  )}

                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <Separator className="my-4" />

      <div className="overflow-x-auto lg:max-w-[1200px] max-w-[380px] md:max-w-[580px] lg:h-[65vh] h-[58vh] md:h-[70vh]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">Sl No.</TableHead>
              <TableHead className="w-[300px]">Title</TableHead>
              <TableHead>Banner</TableHead>
              <TableHead>Added on</TableHead>
              <TableHead className="w-[120px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentItems.length > 0 ? (
              currentItems.map((category, index) => (
                <TableRow key={category._id}>
                  <TableCell>{indexOfFirstItem + index + 1}</TableCell>
                  <TableCell className="font-medium truncate max-w-[250px]">
                    {category.title}
                  </TableCell>
                  <TableCell>
                    <img
                      src={`${category.image}`}
                      alt={category.title}
                      className="w-20 h-10 object-cover rounded"
                    />
                  </TableCell>
                  <TableCell>{formatDate(category.createdAt)}</TableCell>
                  <TableCell>
                    <div className="flex gap-4 items-center">
                      <Dialog open={openEdit} onOpenChange={setOpenEdit}>
                        <DialogTrigger onClick={() => setUpdateCategory(category.title)}>
                          <Edit className="text-blue-500 size-5 cursor-pointer hover:text-blue-700" />
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-[425px]">
                          <DialogHeader>
                            <DialogTitle className="lg:text-2xl text-xl">
                              Edit Banner
                            </DialogTitle>
                          </DialogHeader>
                          <div className="flex flex-col py-4 space-y-3">
                            <div className="flex items-center gap-4">
                              <Label htmlFor="name" className="w-44">
                                Banner Title
                              </Label>
                              <Input
                                id="name"
                                type="text"
                                value={updateCategory}
                                onChange={(e) => setUpdateCategory(e.target.value)}
                                className="col-span-3"
                              />
                            </div>
                            <div className="flex items-center gap-4">
                              <Label htmlFor="icon" className="w-44">
                                Banner Image
                              </Label>
                              <Input
                                id="icon"
                                type="file"
                                onChange={handleUpdateFileChange}
                                className="col-span-3"
                                accept="image/*"
                              />
                            </div>
                          </div>
                          <div className="flex justify-center items-center">
                            <Button
                              type="submit"
                              className="bg-black px-5 text-white h-9 hover:opacity-90 flex items-center justify-center gap-2"
                              onClick={() => handleUpdate(category)}
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? (
                                <>
                                  <Spinner />
                                  Saving...
                                </>
                              ) : (
                                "Save Changes"
                              )}

                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>

                      <Dialog open={openDelete} onOpenChange={setOpenDelete}>
                        <DialogTrigger>
                          <DeleteIcon className="text-red-500 size-5 cursor-pointer hover:text-red-700" />
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>
                              Delete Banner?
                            </DialogTitle>
                            <DialogDescription>
                              This will permanently delete the banner. This action cannot be undone.
                            </DialogDescription>
                          </DialogHeader>
                          <DialogFooter>
                            <Button
                              variant="outline"
                              onClick={() => setOpenDelete(false)}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="destructive"
                              onClick={() => handleDelete(category)}
                              className="flex items-center justify-center gap-2"
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? (
                                <>
                                  <Spinner />
                                  Deleting...
                                </>
                              ) : (
                                "Delete Banner"
                              )}

                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  {isLoading && <div className="flex items-center justify-center gap-2">
                    <Spinner />

                  </div>}
                  {searchTerm ? "No banners match your search" : "No banners found"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {filteredCategories.length > ITEMS_PER_PAGE && (
        <div className="mt-6">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    prevPage();
                  }}
                  className={currentPage === 1 ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                />
              </PaginationItem>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                // Show first pages, current page with neighbors, or last pages
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <PaginationItem key={pageNum}>
                    <PaginationLink
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        paginate(pageNum);
                      }}
                      isActive={currentPage === pageNum}
                      className={currentPage === pageNum ? "bg-black text-white" : ""}
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}

              {totalPages > 5 && currentPage < totalPages - 2 && (
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
              )}

              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    nextPage();
                  }}
                  className={currentPage === totalPages ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default Banner;